import { http } from "../../../shared/api/http"

export type ModerationStatus =
  | "on_moderation"
  | "approved"
  | "rejected"

export type RelevanceStatus = "active" | "needs_update" | "outdated"

export type VerificationType = "none" | "system" | "buyer"

export type EnterpriseType = "factory" | "trading" | "manufacturer" | "market" | "other"

export type EnterpriseSection = {
  title: string
  items: string[]
}

export type EnterpriseTranslation = {
  name?: string | null
  description?: string | null
  specialization?: string | null
  production_capacity?: string | null
}

export type EnterpriseItem = {
  id: string
  name: string
  type: EnterpriseType
  logo?: string | null
  tags?: string[]
  address?: string | null
  description?: string | null
  moderation_status: ModerationStatus
  verification_type: VerificationType
  relevance_status: RelevanceStatus
  translations?: Record<string, EnterpriseTranslation>
}

/** Full shape (matches backend `Enterprise` schema) — fetched separately for the edit drawer. */
export type EnterpriseDetail = EnterpriseItem & {
  specialization?: string | null
  production_capacity?: string | null
  phone?: string | null
  email?: string | null
  wechat?: string | null
  whatsapp?: string | null
  telegram?: string | null
  category_id?: string | null
  media?: string[]
  sections?: EnterpriseSection[]
  translations: Record<string, EnterpriseTranslation>
}

export type EnterpriseListResponse = {
  enterprises: EnterpriseItem[]
  total?: number
}

export type EnterpriseFilters = {
  limit?: number
  offset?: number
  moderation_status?: ModerationStatus | ""
  relevance_status?: RelevanceStatus | ""
  search?: string
}

export const listChinaEnterprisesApi = (filters: EnterpriseFilters = {}) =>
  http.get<EnterpriseListResponse>("/api/admin/china/enterprises", {
    params: filters,
  })

export const getChinaEnterpriseApi = (enterpriseId: string) =>
  http.get<EnterpriseDetail>(`/api/admin/china/enterprises/${enterpriseId}`)

export type EnterpriseStatusPayload = {
  moderation_status?: ModerationStatus
  verification_type?: VerificationType
  relevance_status?: RelevanceStatus
}

export const updateChinaEnterpriseStatusApi = (
  enterpriseId: string,
  payload: EnterpriseStatusPayload
) =>
  http.patch(`/api/admin/china/enterprises/${enterpriseId}/status`, payload)

export const deleteChinaEnterpriseApi = (enterpriseId: string) =>
  http.delete(`/api/admin/china/enterprises/${enterpriseId}`)

/* ── Create / edit (full form) ── */

export type EnterpriseFormPayload = {
  name: string
  type: EnterpriseType
  description?: string | null
  specialization?: string | null
  production_capacity?: string | null
  address?: string | null
  phone?: string | null
  email?: string | null
  wechat?: string | null
  whatsapp?: string | null
  telegram?: string | null
  category_id?: string | null
  logo_id?: string | null
  media_ids?: string[]
  tags?: string[]
  sections?: EnterpriseSection[]
}

export const createChinaEnterpriseApi = (payload: EnterpriseFormPayload) =>
  http.post<EnterpriseDetail>("/api/admin/china/enterprises", payload)

/** Partial update — only include the keys that actually changed (backend treats an absent/null key as "don't touch"). */
export type EnterpriseUpdatePayload = Partial<Omit<EnterpriseFormPayload, "media_ids">> & {
  clear_tags?: boolean
  clear_sections?: boolean
}

export const updateChinaEnterpriseApi = (enterpriseId: string, payload: EnterpriseUpdatePayload) =>
  http.patch<EnterpriseDetail>(`/api/admin/china/enterprises/${enterpriseId}`, payload)

/* ── Translations ── */

export type EnterpriseTranslationPayload = {
  name?: string | null
  description?: string | null
  specialization?: string | null
  production_capacity?: string | null
}

export const updateChinaEnterpriseTranslationApi = (
  enterpriseId: string,
  lang: string,
  payload: EnterpriseTranslationPayload
) =>
  http.patch<EnterpriseTranslation>(`/api/admin/china/enterprises/${enterpriseId}/translations/${lang}`, payload)

/* ── Uploads ── */

export type EnterpriseUploadResponse = { id: string; url?: string }

export const uploadEnterpriseLogoApi = (file: File) => {
  const formData = new FormData()
  formData.append("file", file)
  return http.post<EnterpriseUploadResponse>("/api/china/upload/logo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

/* ── Categories ── */

export type ChinaCategory = {
  id: string
  codename: string
  parent_id?: string | null
}

export const listChinaCategoriesApi = () =>
  http.get<{ items: ChinaCategory[] }>("/api/china/categories")

/* ── Bulk upload (Excel/CSV) ── */

export type BulkCreateResponse = {
  created_count: number
  errors: string[]
}

export const bulkCreateEnterprisesApi = (file: File) => {
  const formData = new FormData()
  formData.append("file", file)
  return http.post<BulkCreateResponse>("/api/admin/china/enterprises/bulk_create", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

/* ── Bulk delete ── */

export type BulkDeleteResponse = {
  deleted_count: number
}

export const bulkDeleteEnterprisesApi = (ids: string[]) =>
  http.post<BulkDeleteResponse>("/api/admin/china/enterprises/bulk_delete", { ids })